import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";
import { LINKS } from "../../data/content";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Full name", type: "text", required: true },
  { name: "email", label: "Email address", type: "email", required: true },
  { name: "company", label: "Company (optional)", type: "text", required: false },
  { name: "subject", label: "Subject (optional)", type: "text", required: false },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Message sent.");
      setForm({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative bg-[#0c2c20] py-28 md:py-40">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Overline>08 — Get in touch</Overline>
          <Reveal>
            <h2 className="mt-6 font-display text-4xl font-bold leading-[1.02] text-white sm:text-5xl lg:text-6xl">
              Let&apos;s build something
              <span className="font-serif-e italic text-[#c9a24e]"> commercial.</span>
            </h2>
          </Reveal>
          <p className="mt-8 max-w-md text-[15px] leading-relaxed text-[#a1a1aa]">
            Open to partnerships, investment conversations, operational leadership and executive
            opportunities across renewable energy, agriculture and international business.
          </p>
          <div className="mt-10 space-y-4">
            <a
              data-testid="contact-linkedin"
              href={LINKS.linkedin}
              target="_blank"
              rel="noreferrer"
              className="group flex items-center justify-between border-b border-white/10 pb-4 text-white transition-colors hover:text-[#c9a24e]"
            >
              <span className="text-sm uppercase tracking-[0.2em]">LinkedIn</span>
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              data-testid="contact-website"
              href={LINKS.website}
              target="_blank"
              rel="noreferrer"
              className="group flex items-center justify-between border-b border-white/10 pb-4 text-white transition-colors hover:text-[#c9a24e]"
            >
              <span className="text-sm uppercase tracking-[0.2em]">raienergy.com.au</span>
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <form data-testid="contact-form" onSubmit={submit} className="space-y-8">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.name === "message" ? "sm:col-span-2" : ""}>
                  <label className="mb-3 block text-[11px] uppercase tracking-[0.25em] text-[#52525b]">
                    {f.label}
                  </label>
                  <input
                    data-testid={`contact-${f.name}`}
                    type={f.type}
                    value={form[f.name]}
                    onChange={update(f.name)}
                    className="w-full border-0 border-b border-white/15 bg-transparent pb-3 text-white outline-none transition-colors placeholder:text-[#3f3f46] focus:border-[#c9a24e]"
                  />
                </div>
              ))}
            </div>
            <div>
              <label className="mb-3 block text-[11px] uppercase tracking-[0.25em] text-[#52525b]">
                Message
              </label>
              <textarea
                data-testid="contact-message"
                rows={4}
                value={form.message}
                onChange={update("message")}
                className="w-full resize-none border-0 border-b border-white/15 bg-transparent pb-3 text-white outline-none transition-colors placeholder:text-[#3f3f46] focus:border-[#c9a24e]"
              />
            </div>
            <button
              data-testid="contact-submit"
              type="submit"
              disabled={loading}
              className="group inline-flex items-center gap-4 bg-white px-8 py-4 text-sm uppercase tracking-[0.2em] text-black transition-colors hover:bg-[#c9a24e] disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Sending
                </>
              ) : (
                <>
                  Send message
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
